import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useActOnPriceResearch, getListPriceResearchQueryKey, type PriceResearchFile } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Download, FileSpreadsheet, Loader2, Save, ScanSearch } from "lucide-react";
import { errorMessage } from "@/components/cost-recipe-panel";
import {
  asDoc, autoMapping, gbp, parseCsvHeader, requiredColumns, templateCsv, templateHeaders, type ImportPreview, type ResearchType,
} from "@/lib/price-review";

const UNMAPPED = "__none__";

export function downloadPriceTemplate(type: ResearchType = "materials") {
  const blob = new Blob([templateCsv(type)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = type === "labour" ? "labour-research-template.csv" : "price-research-template.csv";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type Loaded = { name: string; content: string };
async function readFiles(list: FileList | null): Promise<Loaded[]> {
  if (!list) return [];
  return Promise.all(Array.from(list).map(async (f) => ({ name: f.name, content: await f.text() })));
}

export function PriceReviewImportDialog({ open, onOpenChange, onSaved, researchType = "materials" }: { open: boolean; onOpenChange: (o: boolean) => void; onSaved: (batchId: string | null) => void; researchType?: ResearchType }) {
  const isLabour = researchType === "labour";
  const HEADERS = templateHeaders(researchType);
  const REQUIRED = requiredColumns(researchType);
  const noun = isLabour ? "labour" : "price";
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [prices, setPrices] = useState<Loaded | null>(null);
  const [factors, setFactors] = useState<Loaded | null>(null);
  const [notes, setNotes] = useState<Loaded[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const previewM = useActOnPriceResearch();
  const saveM = useActOnPriceResearch();

  const headers = useMemo(() => (prices ? parseCsvHeader(prices.content) : []), [prices]);
  const missingRequired = REQUIRED.filter((c) => !mapping[c]);

  function reset() { setName(""); setPrices(null); setFactors(null); setNotes([]); setMapping({}); setPreview(null); }
  function invalidatePreview() { setPreview(null); }

  function body(action: "preview-import" | "import") {
    const files: PriceResearchFile[] = [];
    if (prices) files.push({ name: prices.name, kind: "prices", content: prices.content });
    if (factors && !isLabour) files.push({ name: factors.name, kind: "factors", content: factors.content });
    for (const n of notes) files.push({ name: n.name, kind: "notes", content: n.content });
    const map: Record<string, string> = {};
    for (const [k, v] of Object.entries(mapping)) if (v) map[k] = v;
    return { data: { action, researchType, name: name.trim() || prices?.name || (isLabour ? "Labour research" : "Price research"), files, mapping: map } };
  }

  function runPreview() {
    previewM.mutate(body("preview-import"), {
      onSuccess: (d) => { const p = asDoc<ImportPreview>(d); setPreview({ rows: p.rows ?? [], warnings: p.warnings ?? [], fingerprint: p.fingerprint ?? "" }); },
      onError: (e) => toast({ title: "Preview failed", description: errorMessage(e), variant: "destructive" }),
    });
  }
  function runSave() {
    saveM.mutate(body("import"), {
      onSuccess: (d) => {
        const doc = asDoc<{ id?: string }>(d);
        toast({ title: "Research batch saved", description: `No library ${isLabour ? "allowances" : "prices"} changed. Review and approve rows before applying.` });
        void qc.invalidateQueries({ queryKey: getListPriceResearchQueryKey() });
        reset(); onOpenChange(false); onSaved(doc?.id ?? null);
      },
      onError: (e) => toast({ title: "Could not save batch", description: errorMessage(e), variant: "destructive" }),
    });
  }

  const unresolvedCount = preview?.rows.filter((r) => r.unresolved).length ?? 0;
  const matched = preview?.rows.filter((r) => r.targetKey).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saveM.isPending) onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" /> Import {noun} research</DialogTitle>
          <DialogDescription>Upload, map and preview. Saving stores a research batch only; nothing is approved or applied.{isLabour ? " Allowances are per work-item unit. Hour, day or crew rates are flagged, never converted, unless a productivity note evidences the basis." : ""}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="pr-name">Batch name</Label>
            <Input id="pr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Merchant check, spring" data-testid="input-batch-name" />
          </div>
          <div className="space-y-1.5">
            <Label>{isLabour ? "Labour" : "Price"} CSV <span className="text-destructive">*</span></Label>
            <Input type="file" accept=".csv,text/csv" data-testid="input-file-prices" onChange={async (e) => {
              const [f] = await readFiles(e.target.files); setPrices(f ?? null); setMapping(f ? autoMapping(parseCsvHeader(f.content), researchType) : {}); invalidatePreview();
            }} />
            {prices && <p className="text-xs text-muted-foreground" data-testid="text-prices-file">{prices.name} · {headers.length} columns</p>}
          </div>
          {!isLabour && (
            <div className="space-y-1.5">
              <Label>Factor CSV <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input type="file" accept=".csv,text/csv" data-testid="input-file-factors" onChange={async (e) => { const [f] = await readFiles(e.target.files); setFactors(f ?? null); invalidatePreview(); }} />
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Supporting notes <span className="text-muted-foreground font-normal">(.md or .txt, multiple)</span></Label>
            <Input type="file" multiple accept=".md,.txt,text/plain,text/markdown" data-testid="input-file-notes" onChange={async (e) => { setNotes(await readFiles(e.target.files)); invalidatePreview(); }} />
            {notes.length > 0 && <p className="text-xs text-muted-foreground">{notes.map((n) => n.name).join(", ")}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <p className="text-xs text-muted-foreground">Need the format? The template carries headers only.</p>
          <Button variant="outline" size="sm" onClick={() => downloadPriceTemplate(researchType)} data-testid="button-download-template"><Download className="h-4 w-4 mr-1.5" /> Template CSV</Button>
        </div>

        {prices && (
          <div className="space-y-2" data-testid="section-mapping">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold">Column mapping</h3>
              <span className="text-xs text-muted-foreground">Supported column on the left, your header on the right</span>
            </div>
            <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
              {HEADERS.map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <code className={`text-[11px] w-44 shrink-0 truncate ${REQUIRED.includes(c) ? "font-semibold" : "text-muted-foreground"}`}>{c}{REQUIRED.includes(c) ? " *" : ""}</code>
                  <Select value={mapping[c] ?? UNMAPPED} onValueChange={(v) => { setMapping((m) => { const n = { ...m }; if (v === UNMAPPED) delete n[c]; else n[c] = v; return n; }); invalidatePreview(); }}>
                    <SelectTrigger className="h-7 text-xs" data-testid={`select-map-${c}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={UNMAPPED} className="text-xs">Not mapped</SelectItem>
                      {headers.map((h) => <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {missingRequired.length > 0 && <p className="text-xs text-destructive" data-testid="text-mapping-missing">Map required columns: {missingRequired.join(", ")}</p>}
          </div>
        )}

        {preview && (
          <div className="space-y-2 border-t pt-3" data-testid="section-import-preview">
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline">{preview.rows.length} proposals</Badge>
              <Badge variant="outline">{matched} matched to library</Badge>
              <Badge variant="outline" className={unresolvedCount ? "border-amber-400 text-amber-800 dark:text-amber-300" : ""}>{unresolvedCount} unresolved</Badge>
              {preview.fingerprint && <span className="text-muted-foreground font-mono">fp {preview.fingerprint.slice(0, 12)}</span>}
            </div>
            {preview.warnings.length > 0 && (
              <ul className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs space-y-0.5 max-h-32 overflow-y-auto" data-testid="list-import-warnings">
                {preview.warnings.map((w, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />{w}</li>)}
              </ul>
            )}
            <div className="max-h-72 overflow-auto rounded-md border">
              <Table className="text-xs">
                <TableHeader><TableRow><TableHead>{isLabour ? "Allowance" : "Material"}</TableHead><TableHead>Linked item</TableHead><TableHead className="text-right">Current</TableHead><TableHead className="text-right">Proposed</TableHead><TableHead>Verdict</TableHead><TableHead>Match</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 200).map((r) => (
                    <TableRow key={r.id} data-testid={`row-preview-${r.id}`}>
                      <TableCell><span className="font-medium">{r.materialName}</span><span className="text-muted-foreground"> {r.spec} · {r.unit}</span></TableCell>
                      <TableCell className="text-muted-foreground">{r.taskName || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{gbp(r.currentPrice)}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.kind === "factor" ? `x${r.proposedFactor ?? "—"}` : gbp(r.proposedPrice)}</TableCell>
                      <TableCell>{r.verdict}</TableCell>
                      <TableCell>{r.unresolved || !r.targetKey ? <span className="text-amber-700 dark:text-amber-400">Needs resolution</span> : "Matched"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {preview.rows.length > 200 && <p className="text-xs text-muted-foreground">Showing first 200 of {preview.rows.length}.</p>}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={runPreview} disabled={!prices || missingRequired.length > 0 || previewM.isPending} data-testid="button-preview-import">
            {previewM.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-1.5" />} Preview
          </Button>
          <Button onClick={runSave} disabled={!preview || saveM.isPending} data-testid="button-save-import">
            {saveM.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save batch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
